/**
 * Written-answer input shared by the dialogue and the quiz.
 *
 * Every written question, in any subject, can be handwritten on the pad or
 * typed. Maths questions (answer_mode "handwriting") label the typed mode
 * "Type Math" and use maths placeholders; both modes have the symbol toolbar
 * and live preview. Rough working is sent with the answer to the marker.
 */
import { useRef, useState } from "react";
import { HandwritingPad } from "./HandwritingPad";
import { MathText } from "./Math";

export type MathsAnswerMode = "handwrite" | "type";

/** Maths opens on the pad; other subjects open on typing, pad one tap away. */
export function defaultAnswerMode(answerMode?: string): MathsAnswerMode {
  return answerMode === "handwriting" ? "handwrite" : "type";
}

export const MATH_SHORTCUTS = [
  { label: "x²", insert: "²" },
  { label: "x³", insert: "³" },
  { label: "xⁿ", insert: "^{n}" },
  { label: "√x", insert: "\\sqrt{x}" },
  { label: "a/b", insert: "\\frac{a}{b}" },
  { label: "π", insert: "\\pi" },
  { label: "θ", insert: "\\theta" },
  { label: "±", insert: "\\pm" },
  { label: "≤", insert: "\\le" },
  { label: "≥", insert: "\\ge" },
  { label: "×", insert: "\\times" },
  { label: "÷", insert: "\\div" },
  { label: "≠", insert: "\\ne" },
  { label: "∞", insert: "\\infty" },
];

/* The pad's pencil cursor leans the way a right-handed writer holds a pencil;
   left-handed learners get it mirrored. Remembered on this device only. */
const HAND_KEY = "pydialog.handedness";

function readLeftHanded(): boolean {
  try { return localStorage.getItem(HAND_KEY) === "left"; } catch { return false; }
}

export function MathsAnswerInput({
  maths,
  answer,
  onAnswerChange,
  ink,
  onInkChange,
  mode,
  onModeChange,
  working,
  onWorkingChange,
  disabled = false,
  onKeyDown,
}: {
  maths: boolean;
  answer: string;
  onAnswerChange: (value: string) => void;
  ink: string;
  onInkChange: (value: string) => void;
  mode: MathsAnswerMode;
  onModeChange: (mode: MathsAnswerMode) => void;
  working: string;
  onWorkingChange: (value: string) => void;
  disabled?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showWorking, setShowWorking] = useState(Boolean(working));
  const [leftHanded, setLeftHanded] = useState(readLeftHanded);

  const toggleHand = () => {
    const next = !leftHanded;
    setLeftHanded(next);
    try { localStorage.setItem(HAND_KEY, next ? "left" : "right"); } catch { /* storage blocked */ }
  };

  const insert = (snippet: string) => {
    const ta = textareaRef.current;
    if (!ta) {
      onAnswerChange(answer + snippet);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    onAnswerChange(answer.substring(0, start) + snippet + answer.substring(end));
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + snippet.length, start + snippet.length);
    }, 0);
  };

  const typed = (placeholder: string, previewLabel: string, toolbarLabel: string) => (
    <div className={maths ? "typed-math-area" : "typed-section"}>
      <div className="math-toolbar" aria-label="Math formula shortcuts">
        <span className="toolbar-label">{toolbarLabel}</span>
        <div className="math-toolbar-buttons">
          {MATH_SHORTCUTS.map((s) => (
            <button
              key={s.label}
              type="button"
              className="math-symbol-btn"
              onClick={() => insert(s.insert)}
              title={`Insert ${s.label}`}
              disabled={disabled}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <textarea
        ref={textareaRef}
        className="answer-textarea"
        value={answer}
        onChange={(e) => onAnswerChange(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        rows={6}
      />
      {answer.trim() && (
        <div className="math-live-preview">
          <span className="preview-label">{previewLabel}</span>
          <div className="preview-content">
            <MathText content={answer} />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="handwriting-section">
          <div className="scratchpad-modes" role="tablist">
            <button
              type="button"
              className={`scratchpad-mode-btn ${mode === "handwrite" ? "active" : ""}`}
              onClick={() => onModeChange("handwrite")}
              disabled={disabled}
            >
              ✏️ Handwrite (Stylus/Mouse)
            </button>
            <button
              type="button"
              className={`scratchpad-mode-btn ${mode === "type" ? "active" : ""}`}
              onClick={() => onModeChange("type")}
              disabled={disabled}
            >
              {maths ? "⌨️ Type Math" : "⌨️ Type"}
            </button>
            {mode === "handwrite" && (
              <button
                type="button"
                className={`scratchpad-mode-btn scratchpad-hand ${leftHanded ? "active" : ""}`}
                onClick={toggleHand}
                disabled={disabled}
                aria-pressed={leftHanded}
              >
                Left-handed
              </button>
            )}
          </div>
          {mode === "handwrite" ? (
            <HandwritingPad
              value={ink}
              onChange={onInkChange}
              disabled={disabled}
              leftHanded={leftHanded}
              placeholder={maths ? "Write your mathematical derivation or explanation here..." : "Write your answer here..."}
            />
          ) : maths ? (
            typed("Type your explanation or mathematical proof...", "Live Formula Preview:", "Symbols:")
          ) : (
            typed("Explain your thinking clearly in your own words...", "Live Preview:", "Math Symbols:")
          )}
      </div>

      <div className="working-toggle-area">
        <button
          type="button"
          className="btn-toggle-working"
          onClick={() => setShowWorking((v) => !v)}
          disabled={disabled}
        >
          {showWorking ? "Hide Rough Working" : "+ Add Rough Working Notes"}
        </button>
        {showWorking && (
          <textarea
            className="working-textarea"
            value={working}
            onChange={(e) => onWorkingChange(e.target.value)}
            disabled={disabled}
            placeholder="Scratchpad / rough notes (will be recorded with your answer)..."
            rows={3}
          />
        )}
      </div>
    </>
  );
}
